import { MakeLogicType, LogicWrapper, actions, connect, kea, key, listeners, path, props, selectors } from 'kea'
import { forms } from 'kea-forms'
import type { DeepPartial, DeepPartialMap, FieldName, ValidationErrorType } from 'kea-forms'

import { propertyFilterTypeToPropertyDefinitionType } from 'lib/components/PropertyFilters/utils'
import { uuid } from 'lib/utils/dom'

import { propertyDefinitionsModel } from '~/models/propertyDefinitionsModel'
import { PropertyFilterType, PropertyOperator, QuickFilter, QuickFilterOption } from '~/types'

import type { Option } from '../../../models/propertyDefinitionsModel'
import type { PropertyDefinitionType } from '../../../types'
import { quickFiltersLogic } from './quickFiltersLogic'
import { ModalView, QuickFiltersModalLogicProps, quickFiltersModalLogic } from './quickFiltersModalLogic'

export interface QuickFilterFormLogicProps extends QuickFiltersModalLogicProps {
    filter: QuickFilter | null
}

export interface QuickFilterFormValues {
    name: string
    propertyName: string
    options: QuickFilterOption[]
}

export const allowedOperators = [
    PropertyOperator.Exact,
    PropertyOperator.IsNot,
    PropertyOperator.IContains,
    PropertyOperator.NotIContains,
    PropertyOperator.Regex,
    PropertyOperator.IsSet,
    PropertyOperator.IsNotSet,
]

const trimValue = (value: string | string[] | null): string | string[] | null => {
    if (value === null) {
        return null
    }
    if (Array.isArray(value)) {
        return value.map((v) => v.trim())
    }
    return value.trim()
}

export const operatorsWithoutValues = [PropertyOperator.IsSet, PropertyOperator.IsNotSet]

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface quickFilterFormLogicValues {
    propertyOptions: Record<string, Option> // propertyDefinitionsModel
    isQuickFilterSubmitting: boolean
    isQuickFilterValid: boolean
    name: string
    options: QuickFilterOption[]
    propertyName: string
    quickFilter: QuickFilterFormValues
    quickFilterAllErrors: Record<string, any>
    quickFilterChanged: boolean
    quickFilterErrors: DeepPartialMap<QuickFilterFormValues, ValidationErrorType>
    quickFilterHasErrors: boolean
    quickFilterManualErrors: Record<string, any>
    quickFilterTouched: boolean
    quickFilterTouches: Record<string, boolean>
    quickFilterValidationErrors: DeepPartialMap<QuickFilterFormValues, ValidationErrorType>
    showQuickFilterErrors: boolean
    suggestions: any[]
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface quickFilterFormLogicActions {
    loadPropertyValues: (payload: {
        endpoint: string | undefined
        eventNames?: string[]
        newInput: string | undefined
        properties?: {
            key: string
            values: string | string[]
        }[]
        propertyKey: string
        refresh?: string
        type: PropertyDefinitionType
    }) => {
        endpoint: string | undefined
        eventNames?: string[] | undefined
        newInput: string | undefined
        properties?:
            | {
                  key: string
                  values: string | string[]
              }[]
            | undefined
        propertyKey: string
        refresh?: string | undefined
        type: PropertyDefinitionType
    } // propertyDefinitionsModel
    closeModal: () => {
        value: true
    } // quickFiltersModalLogic
    setView: (view: ModalView) => {
        view: ModalView
    } // quickFiltersModalLogic
    addOption: () => {
        value: true
    }
    removeOption: (index: number) => {
        index: number
    }
    resetQuickFilter: (values?: QuickFilterFormValues) => {
        values?: QuickFilterFormValues
    }
    setQuickFilterManualErrors: (errors: Record<string, any>) => {
        errors: Record<string, any>
    }
    setQuickFilterValue: (
        key: FieldName,
        value: any
    ) => {
        name: FieldName
        value: any
    }
    setQuickFilterValues: (values: DeepPartial<QuickFilterFormValues>) => {
        values: DeepPartial<QuickFilterFormValues>
    }
    submitQuickFilter: () => {
        value: boolean
    }
    submitQuickFilterFailure: (
        error: Error,
        errors: Record<string, any>
    ) => {
        error: Error
        errors: Record<string, any>
    }
    submitQuickFilterRequest: (quickFilter: QuickFilterFormValues) => {
        quickFilter: QuickFilterFormValues
    }
    submitQuickFilterSuccess: (quickFilter: QuickFilterFormValues) => {
        quickFilter: QuickFilterFormValues
    }
    touchQuickFilterField: (key: string) => {
        key: string
    }
    updateOption: (
        index: number,
        updates: Partial<QuickFilterOption>
    ) => {
        index: number
        updates: Partial<QuickFilterOption>
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface quickFilterFormLogicMeta {
    key: string
    __keaTypeGenInternalSelectorTypes: {
        name: (quickFilter: QuickFilterFormValues) => string
        propertyName: (quickFilter: QuickFilterFormValues) => string
        options: (quickFilter: QuickFilterFormValues) => QuickFilterOption[]
        suggestions: (propertyName: string, propertyOptions: Record<string, Option>) => any[]
    }
}

export type quickFilterFormLogicType = MakeLogicType<
    quickFilterFormLogicValues,
    quickFilterFormLogicActions,
    QuickFilterFormLogicProps,
    quickFilterFormLogicMeta
>

export const quickFilterFormLogic: LogicWrapper<quickFilterFormLogicType> = kea<quickFilterFormLogicType>([
    path(['lib', 'components', 'QuickFilters', 'quickFilterFormLogic']),
    props({} as QuickFilterFormLogicProps),
    key((props) => `${props.context}-${props.filter?.id || 'new'}`),

    connect((props: QuickFilterFormLogicProps) => ({
        values: [propertyDefinitionsModel, ['options as propertyOptions']],
        actions: [
            propertyDefinitionsModel,
            ['loadPropertyValues'],
            quickFiltersModalLogic(props),
            ['setView', 'closeModal'],
        ],
    })),

    actions({
        addOption: true,
        removeOption: (index: number) => ({ index }),
        updateOption: (index: number, updates: Partial<QuickFilterOption>) => ({ index, updates }),
    }),

    forms(({ actions, props }) => ({
        quickFilter: {
            defaults: {
                name: props.filter?.name || '',
                propertyName: props.filter?.property_name || '',
                options: (props.filter?.options || [
                    { id: uuid(), value: null, label: '', operator: PropertyOperator.Exact },
                ]) as QuickFilterOption[],
            } as QuickFilterFormValues,
            errors: ({ name, propertyName, options }) => {
                const errors: Record<string, string | object | undefined> = {}

                if (!name.trim()) {
                    errors.name = 'Filter name is required'
                }

                if (!propertyName.trim()) {
                    errors.propertyName = 'Event property is required'
                }

                if (options.length === 0) {
                    errors.options = 'At least one option is required'
                } else {
                    const optionErrors: Record<string, string>[] = []
                    let hasOptionErrors = false

                    options.forEach((opt: QuickFilterOption, index: number) => {
                        const optError: Record<string, string> = {}

                        if (!opt.label.trim()) {
                            optError.label = 'Display name is required'
                            hasOptionErrors = true
                        }

                        const operatorNeedsValue = !operatorsWithoutValues.includes(opt.operator as PropertyOperator)
                        if (operatorNeedsValue) {
                            if (!opt.value || (Array.isArray(opt.value) && opt.value.length === 0)) {
                                optError.value = 'Value is required'
                                hasOptionErrors = true
                            }
                        }

                        optionErrors[index] = optError
                    })

                    if (hasOptionErrors) {
                        errors.options = optionErrors
                    }
                }

                return errors
            },
            submit: async ({ name, propertyName, options }) => {
                const payload = {
                    name: name.trim(),
                    property_name: propertyName.trim(),
                    type: 'manual-options' as const,
                    options: options.map((opt: QuickFilterOption) => ({
                        id: opt.id,
                        value: trimValue(opt.value),
                        label: opt.label.trim(),
                        operator: opt.operator || PropertyOperator.Exact,
                    })),
                }

                if (props.filter) {
                    await quickFiltersLogic(props).asyncActions.updateFilter(props.filter.id, payload)
                } else {
                    await quickFiltersLogic(props).asyncActions.createFilter(payload)
                }

                // Return to list view instead of closing modal
                // This allows the auto-select logic to trigger for new filters and users to add more filters
                actions.setView(ModalView.List)
            },
        },
    })),

    selectors({
        name: [(s) => [s.quickFilter], (quickFilter: QuickFilterFormValues) => quickFilter.name],
        propertyName: [(s) => [s.quickFilter], (quickFilter: QuickFilterFormValues) => quickFilter.propertyName],
        options: [(s) => [s.quickFilter], (quickFilter: QuickFilterFormValues) => quickFilter.options],
        suggestions: [
            (s) => [s.propertyName, s.propertyOptions],
            (
                propertyName: string,
                propertyOptions: Record<string, import('~/models/propertyDefinitionsModel').Option>
            ): any[] => {
                return propertyName ? propertyOptions[propertyName]?.values || [] : []
            },
        ],
    }),

    listeners(({ actions, values }) => ({
        setQuickFilterValue: ({ name, value }) => {
            if (name === 'propertyName' && value) {
                actions.loadPropertyValues({
                    endpoint: undefined,
                    type: propertyFilterTypeToPropertyDefinitionType(PropertyFilterType.Event),
                    newInput: '',
                    propertyKey: value as string,
                    eventNames: [],
                    properties: [],
                })
            }

            // Auto-fill label when value is set and label is empty
            if (Array.isArray(name) && name[0] === 'options' && name[2] === 'value') {
                const optionIndex = name[1] as number
                const currentOption = values.options[optionIndex]
                if (currentOption && !currentOption.label) {
                    const autoLabel = Array.isArray(value) ? (value as string[]).join(', ') : ((value as string) ?? '')
                    if (autoLabel) {
                        actions.setQuickFilterValue(['options', optionIndex, 'label'], autoLabel)
                    }
                }
            }
        },
        addOption: () => {
            actions.setQuickFilterValue('options', [
                ...values.options,
                { id: uuid(), value: null, label: '', operator: PropertyOperator.Exact },
            ])
        },
        removeOption: ({ index }) => {
            actions.setQuickFilterValue(
                'options',
                values.options.filter((_, i) => i !== index)
            )
        },
        updateOption: ({ index, updates }) => {
            for (const [key, value] of Object.entries(updates)) {
                actions.setQuickFilterValue(['options', index, key], value)
            }
        },
    })),
])
